import {
  ComponentProps,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'

import { setCookie } from 'nookies'

import { Loader } from 'components'

import { useGetTenantByCode } from 'services/tenant'
import { TenantData, TenantDomain } from 'services/tenant/types'

import { useTheme } from '../useTheme'
import {
  handleBrandColors,
  handleFavicon,
  sanitizeTenant,
} from './utils'

interface TenantContextProps {
  tenant: TenantDomain
  setTenant: (tenant: TenantData) => void
}

const TenantContext = createContext<TenantContextProps | null>(null)

const hostname = window.location.hostname
const isLocalEnvironment = hostname.includes('localhost')

export const TenantProvider = ({
  children,
  ...props
}: ComponentProps<'div'>) => {
  const { theme } = useTheme()

  const hotsname =
    hostname.split('.')[0] === 'portal' ? 'orsegups' : hostname.split('.')[0]

  const tenantCode = isLocalEnvironment ? process.env.TENANT_CODE : hotsname

  const hasTenantDefined =
    !!tenantCode && !['app', 'localhost'].includes(tenantCode)

  const [tenant, setTenant] = useState<TenantData | undefined>(undefined)

  const { data, isLoading } = useGetTenantByCode(
    hasTenantDefined ? tenantCode : '',
  )

  const handleSetHostname = (code: string) => {
    if (!isLocalEnvironment) {
      window.location.href = window.location.href.replace(
        window.location.hostname.split('.')[0],
        code,
      )
    }
  }

  const updateTenantResources = (sanitizedData: TenantData) => {
    setCookie(undefined, 'tenantId', sanitizedData.id, {
      maxAge: 600 / (60 * 24),
    })

    setCookie(undefined, 'tenantCode', sanitizedData.code, {
      maxAge: 600 / (60 * 24),
    })

    sessionStorage.setItem('tenantId', sanitizedData?.id || '')
    sessionStorage.setItem('tenantCode', sanitizedData.code)

    document.documentElement.setAttribute('tenant', sanitizedData.code)

    const { lightTheme, darkTheme } = sanitizedData.brand

    if (lightTheme.color && darkTheme.color) {
      handleBrandColors({ light: lightTheme.color, dark: darkTheme.color })
    }

    if (lightTheme.images.logo && darkTheme.images.logo) {
      handleFavicon(
        theme === 'dark' ? darkTheme.images.logo : lightTheme.images.logo,
      )
    }
  }

  const handleSetTenant = (tenant: TenantData) => {
    const sanitizedData = sanitizeTenant(tenant)
    setTenant(sanitizedData)

    updateTenantResources(sanitizedData)
    handleSetHostname(sanitizedData.code)
  }

  useEffect(() => {
    if (!tenant && data) {
      const sanitizedData = sanitizeTenant(data)
      setTenant(sanitizedData)
      updateTenantResources(sanitizedData)
    }
  }, [data])

  return (
    <TenantContext.Provider
      value={{
        ...(tenant && {
          tenant: {
            ...tenant,
            brand:
              tenant?.brand?.[theme === 'dark' ? 'darkTheme' : 'lightTheme'],
          },
        }),
        setTenant: handleSetTenant,
      }}
      {...props}
    >
      {isLoading ? (
        <Loader appearance="screen" style={{ height: '100vh' }} />
      ) : (
        children
      )}
    </TenantContext.Provider>
  )
}

export const useTenant = () => {
  const context = useContext(TenantContext)

  if (!context) throw new Error('useTenant must be used within TenantProvider')

  return context
}
